/**
 * GET /api/v1/billing/status — THE one contract, shared by server and web.
 *
 * The web imports this file as `@beheard/billing-contract` (web/vite.config.ts
 * and web/tsconfig.json alias it), so the server's response type and the web's
 * read type are the same declaration. Dependency-free by contract: the browser
 * bundle compiles it.
 *
 * Every field names its authority, and nothing unknown is reported as a number:
 *
 *   plan            BeHeard's SESSION CAPACITY TIER — services/plan/capacityTier.ts
 *                   resolveCapacityTier. NONCOMMERCIAL: it sizes local session
 *                   limits; it is NOT the commercial plan and carries no price.
 *                   Clients label it "Session capacity tier"
 *                   (BILLING_CAPACITY_TIER_LABEL); the commercial plan shown is
 *                   Central's `subscription` only. If the tier cannot be read,
 *                   the WHOLE status is unavailable (503
 *                   BILLING_STATUS_UNAVAILABLE): a paying user is never shown
 *                   "Free" because something was down.
 *   capabilities    Central's capability grant for app `beheard`, evaluated by
 *                   the same predicate requireCapability enforces.
 *   subscription    Central's commercial subscription (GET /api/subscription/me),
 *                   or { available: false } when Central cannot be read.
 *   credits         Central credits per BeHeard pool. Text and live conversation
 *                   sessions DO reserve Central credits when FEATURE_TEXT_BILLING /
 *                   FEATURE_LIVE_BILLING is on (off by default); no per-pool
 *                   balance is reported here yet, so every pool is
 *                   { balance: null, reason } — rendered "unavailable", never 0.
 *   safetyAllowance The LOCAL NONCOMMERCIAL SAFETY LIMIT pools UsageService
 *                   actually enforces (types/usage.ts). Not credits: no price,
 *                   no purchase. Unknown → { state: 'unavailable' }.
 *
 * Gating rule for clients: the server is authoritative. A client may DISPLAY
 * these values; it must not block an action on a missing/unknown value, and it
 * shows "limit reached" only when a pool is known to be exhausted (or the
 * server refused).
 */

export const BILLING_STATUS_CONTRACT_VERSION = 2 as const;

export type BillingPlanTier = 'free' | 'pro' | 'pro_ai';

/**
 * How a client labels `plan`: BeHeard's noncommercial session capacity tier —
 * never presented as the commercial plan (that is Central's `subscription`).
 */
export const BILLING_CAPACITY_TIER_LABEL = 'Session capacity tier';
export const BILLING_CAPACITY_TIER_NOTE =
  'Sets your session limits in BeHeard. It is not your billing plan — your subscription is managed in LevelUp.';

export const BILLING_PLAN_NAMES: Readonly<Record<BillingPlanTier, string>> = {
  free: 'Free',
  pro: 'Pro',
  pro_ai: 'Pro + AI',
};

/**
 * Every capability BeHeard checks with requireCapability (Central registry for
 * app `beheard`, docs/CENTRAL-CONTRACT.md B1a). The status reports which of
 * these the caller holds.
 */
export const BEHEARD_CAPABILITY_CHECKS = [
  'admin:read',
  'admin:write',
  'ai:tts',
  'ai:voice',
  'billing:write',
  'coaching:read',
  'coaching:write',
  'endorsement:write',
  'marketplace:listen',
  'marketplace:read',
  'marketplace:write',
  'org:admin',
  'org:read',
  'portfolio:read',
  'portfolio:write',
  'practice:read',
  'practice:write',
  'recruiter:read',
  'recruiter:write',
  'training:read',
  'training:write',
] as const;
export type BeheardCapability = (typeof BEHEARD_CAPABILITY_CHECKS)[number];

export type AllowancePoolKey = 'practice' | 'coaching' | 'listen';
export const ALLOWANCE_POOL_KEYS: readonly AllowancePoolKey[] = ['practice', 'coaching', 'listen'];

export type CreditUnavailableReason = 'central_credit_actions_not_registered';

/** A Central credit pool. `balance: null` = unknown/not applicable — never render it as 0. */
export type CreditPoolStatus =
  | { balance: number; reason?: undefined }
  | { balance: null; reason: CreditUnavailableReason };

export type SafetyAllowanceStatus =
  | { state: 'unlimited' }
  | { state: 'limited'; remaining: number; monthly: number }
  | { state: 'unavailable'; reason: 'usage_authority_unavailable' };

export interface SubscriptionSummary {
  planCode: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export type SubscriptionView =
  | { available: true; subscription: SubscriptionSummary | null }
  | { available: false; reason: 'central_unreachable' | 'central_error' | 'central_rejected_token' };

export interface BillingStatus {
  contractVersion: typeof BILLING_STATUS_CONTRACT_VERSION;
  plan: { tier: BillingPlanTier; name: string; source: 'beheard_capacity_tier' };
  capabilities: { source: 'central_switch_context' | 'jwt_fallback'; granted: BeheardCapability[] };
  subscription: SubscriptionView;
  credits: Record<AllowancePoolKey, CreditPoolStatus>;
  safetyAllowance: {
    authority: 'LOCAL_NONCOMMERCIAL_SAFETY_LIMIT';
    pools: Record<AllowancePoolKey, SafetyAllowanceStatus>;
  };
}

/** The 503 the endpoint answers when the plan tier itself cannot be read. */
export const BILLING_STATUS_UNAVAILABLE_CODE = 'BILLING_STATUS_UNAVAILABLE';
export const BILLING_STATUS_UNAVAILABLE_MESSAGE = 'Billing status temporarily unavailable';

/** No per-pool Central credit balance is reported by this endpoint (see `credits` above). */
export const CENTRAL_CREDITS_NOT_REGISTERED: CreditPoolStatus = {
  balance: null,
  reason: 'central_credit_actions_not_registered',
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Runtime shape check (the contract test runs it on the server's real output
 * and on the web's fixtures). Returns the list of violations; [] = valid.
 */
export function billingStatusViolations(value: unknown): string[] {
  const out: string[] = [];
  if (!isObj(value)) return ['not an object'];
  const v = value;
  const expectedKeys = ['capabilities', 'contractVersion', 'credits', 'plan', 'safetyAllowance', 'subscription'];
  const keys = Object.keys(v).sort();
  if (keys.join(',') !== expectedKeys.join(',')) out.push(`keys ${keys.join(',')} != ${expectedKeys.join(',')}`);
  if (v.contractVersion !== BILLING_STATUS_CONTRACT_VERSION) out.push('contractVersion');

  const plan = v.plan;
  if (!isObj(plan) || !['free', 'pro', 'pro_ai'].includes(plan.tier as string) || typeof plan.name !== 'string' || plan.source !== 'beheard_capacity_tier') {
    out.push('plan');
  }

  const caps = v.capabilities;
  if (
    !isObj(caps) ||
    !['central_switch_context', 'jwt_fallback'].includes(caps.source as string) ||
    !Array.isArray(caps.granted) ||
    caps.granted.some((c) => !(BEHEARD_CAPABILITY_CHECKS as readonly string[]).includes(c as string))
  ) {
    out.push('capabilities');
  }

  const sub = v.subscription;
  if (!isObj(sub) || typeof sub.available !== 'boolean') out.push('subscription');
  else if (sub.available === true) {
    const s = sub.subscription;
    if (s !== null && !(isObj(s) && typeof s.planCode === 'string' && typeof s.status === 'string' && typeof s.cancelAtPeriodEnd === 'boolean' && (s.currentPeriodEnd === null || typeof s.currentPeriodEnd === 'string'))) {
      out.push('subscription.subscription');
    }
  } else if (!['central_unreachable', 'central_error', 'central_rejected_token'].includes(sub.reason as string)) {
    out.push('subscription.reason');
  }

  const credits = v.credits;
  if (!isObj(credits)) out.push('credits');
  else {
    for (const k of ALLOWANCE_POOL_KEYS) {
      const c = credits[k];
      const ok = isObj(c) && ((typeof c.balance === 'number' && c.reason === undefined) || (c.balance === null && typeof c.reason === 'string'));
      if (!ok) out.push(`credits.${k}`);
    }
    if (Object.keys(credits).length !== ALLOWANCE_POOL_KEYS.length) out.push('credits keys');
  }

  const sa = v.safetyAllowance;
  if (!isObj(sa) || sa.authority !== 'LOCAL_NONCOMMERCIAL_SAFETY_LIMIT' || !isObj(sa.pools)) out.push('safetyAllowance');
  else {
    const pools = sa.pools as Record<string, unknown>;
    for (const k of ALLOWANCE_POOL_KEYS) {
      const p = pools[k];
      const ok =
        isObj(p) &&
        ((p.state === 'unlimited') ||
          (p.state === 'limited' && typeof p.remaining === 'number' && typeof p.monthly === 'number') ||
          (p.state === 'unavailable' && p.reason === 'usage_authority_unavailable'));
      if (!ok) out.push(`safetyAllowance.pools.${k}`);
    }
  }
  return out;
}
